import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Onglet « Conversations » du Chat Live.
// Regroupe les échanges des visiteurs par session, affiche le
// déroulé d'une conversation et les questions les plus fréquentes.
// ============================================================

interface ConversationRow {
  id: number;
  session_id: string;
  visitor_id: string | null;
  role: 'user' | 'assistant';
  content: string | null;
  page: string | null;
  created_at: string;
}

interface SessionGroup {
  sessionId: string;
  messages: ConversationRow[];
  firstUser: string;
  page: string;
  lastAt: string;
  userCount: number;
}

export default function ConversationsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('chat_live_conversations')
        .select('id, session_id, visitor_id, role, content, page, created_at')
        .eq('idcommerce', getCommerceId(user))
        .order('created_at', { ascending: true })
        .limit(1000);
      if (fetchErr) throw fetchErr;
      setRows((data as ConversationRow[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des conversations');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const sessions = useMemo<SessionGroup[]>(() => {
    // On regroupe par VISITEUR (son identifiant stable), et non par session :
    // ainsi chaque visiteur — même s'il démarre plusieurs conversations —
    // apparaît dans un SEUL fil persistant côté marchand.
    const map = new Map<string, ConversationRow[]>();
    rows.forEach((r) => {
      const key = r.visitor_id || r.session_id;
      const arr = map.get(key) || [];
      arr.push(r);
      map.set(key, arr);
    });
    const groups: SessionGroup[] = [];
    map.forEach((messages, sessionId) => {
      const firstUser = messages.find((m) => m.role === 'user')?.content || 'Nouvelle conversation';
      groups.push({
        sessionId,
        messages,
        firstUser,
        page: messages[messages.length - 1]?.page || '',
        lastAt: messages[messages.length - 1]?.created_at || '',
        userCount: messages.filter((m) => m.role === 'user').length,
      });
    });
    return groups.sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  }, [rows]);

  const frequentQuestions = useMemo(() => {
    const counts = new Map<string, number>();
    rows.filter((r) => r.role === 'user' && r.content).forEach((r) => {
      const key = (r.content || '').trim().replace(/\s+/g, ' ').slice(0, 80);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([question, count]) => ({ question, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [rows]);

  const activeMessages = useMemo(
    () => (selected ? sessions.find((s) => s.sessionId === selected)?.messages || [] : []),
    [selected, sessions],
  );

  const fmt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={load} className="ml-auto text-xs font-medium underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-500">Conversations</p>
          <p className="text-2xl font-bold text-foreground-950">{sessions.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-500">Messages visiteurs</p>
          <p className="text-2xl font-bold text-foreground-950">{rows.filter((r) => r.role === 'user').length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 col-span-2 lg:col-span-1">
          <p className="text-xs text-foreground-500">Réponses de l’assistant</p>
          <p className="text-2xl font-bold text-foreground-950">{rows.filter((r) => r.role === 'assistant').length}</p>
        </div>
      </div>

      {/* Questions fréquentes */}
      {frequentQuestions.length > 0 && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
          <p className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-question-answer-line text-accent-500"></i>Questions les plus fréquentes
          </p>
          <div className="space-y-2">
            {frequentQuestions.map((q) => (
              <div key={q.question} className="flex items-center gap-3">
                <span className="flex-1 min-w-0 text-sm text-foreground-700 truncate">{q.question}</span>
                <span className="text-xs font-semibold text-secondary-700 bg-secondary-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                  ×{q.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste + détail */}
      {sessions.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-xl">
          <i className="ri-chat-3-line text-4xl text-foreground-300 block mb-3"></i>
          <p className="text-sm text-foreground-600 font-medium">Aucune conversation pour l’instant</p>
          <p className="text-xs text-foreground-400 mt-1">Les échanges des visiteurs avec votre assistant apparaîtront ici.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
              Sessions
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-background-200/70">
              {sessions.map((s) => (
                <button
                  key={s.sessionId}
                  onClick={() => setSelected(s.sessionId)}
                  className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${
                    selected === s.sessionId ? 'bg-primary-50/60' : 'hover:bg-background-100'
                  }`}
                >
                  <p className="text-sm text-foreground-800 line-clamp-2">{s.firstUser}</p>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-foreground-400">
                    <i className="ri-message-3-line"></i>{s.userCount}
                    {s.page && <><span>·</span><span className="truncate max-w-[120px]">{s.page}</span></>}
                    <span className="ml-auto whitespace-nowrap">{fmt(s.lastAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
            {!selected ? (
              <div className="flex-1 flex flex-col items-center justify-center py-20 text-foreground-300 gap-2">
                <i className="ri-arrow-left-line text-2xl"></i>
                <span className="text-xs">Sélectionnez une conversation</span>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-background-200/70 text-xs font-mono text-foreground-400 truncate">
                  {selected}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[520px]">
                  {activeMessages.map((m) => (
                    <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-primary-500 text-background-50 rounded-br-sm'
                          : 'bg-background-100 text-foreground-800 rounded-bl-sm'
                      }`}>
                        {m.content}
                        <span className={`block text-[10px] mt-1 ${m.role === 'user' ? 'text-background-50/70' : 'text-foreground-400'}`}>
                          {fmt(m.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}